import React, { useState, ChangeEvent, useEffect, useRef } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { IconX } from '@tabler/icons-react';
import Title from './ui/Title';

interface SimulatorContentProps {
    selectedFile: string;
    isRunning: boolean;
    logPreview: string[];
    file_names: string[];
    handleFileChange: (event: ChangeEvent<HTMLSelectElement>) => void;
    startSimulation: () => void;
    stopSimulation: () => void;
}

const SimulatorContent = ({
    selectedFile,
    isRunning,
    logPreview,
    file_names,
    handleFileChange,
    startSimulation,
    stopSimulation
}: SimulatorContentProps) => {
    return (
        <div className="flex flex-col space-y-4 p-8">
            <div className="mb-2">
                <label htmlFor="file-select" className="block text-sm font-medium text-gray-700 mb-1">
                    Select Simulation File
                </label>
                <select 
                    id="file-select" 
                    value={selectedFile} 
                    onChange={handleFileChange}
                    className="w-full px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                    <option value="">-- Select a file --</option>
                    {file_names.map((filename, index) => (
                        <option key={index} value={filename}>
                            {filename}
                        </option>
                    ))}
                </select>
            </div>
            
            <div className="flex space-x-3">
                <Button
                    onClick={startSimulation} 
                    disabled={!selectedFile || isRunning}
                >
                    Start Simulation
                </Button>
                <Button 
                    onClick={stopSimulation}
                    disabled={!isRunning}
                >
                    Stop Simulation
                </Button>
            </div>
            
            {/* Preview section */}
            {logPreview.length > 0 && (
                <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Time Log Preview:</h3>
                    <div className="max-h-[400px] overflow-y-auto bg-gray-50 border border-gray-300 rounded-md shadow-inner">
                        <ul className="divide-y divide-gray-200">
                            {logPreview.map((entry, index) => (
                                <li key={index} className="px-4 py-3 hover:bg-gray-100 transition-colors">
                                    <div className="text-sm text-gray-800 font-mono">{entry}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

const Simulator = ({setSelectedItems}: {setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>}) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    
    // Moved state from SimulatorContent to here
    const [selectedFile, setSelectedFile] = useState<string>("");
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const [logPreview, setLogPreview] = useState<string[]>([]);
    const timeoutIdsRef = useRef<number[]>([]);
    
    // File options list
    const file_names: string[] = [
        "meldkamer_case_buitengebied_fietser.csv",
        "meldkamer_case_bosbrand_vuurwerk.csv",
        "meldkamer_case_woningbrand.csv",
        "meldkamer_case_boerderij.csv",
        "meldkamer_log_bewerkt.csv"
    ];
    
    // Clean up timeouts when component unmounts
    useEffect(() => {
        return () => {
            timeoutIdsRef.current.forEach(id => clearTimeout(id));
        };
    }, []);
    
    const handleFileChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const file = event.target.value;
        setSelectedFile(file);
        setIsRunning(false); // Reset simulation when file changes
        
        if (file) {
            loadFilePreview(file);
        } else {
            setLogPreview([]);
        }
    };
    
    const loadFilePreview = (filename: string) => {
        fetch(`vnog_simulations/${filename}`)
            .then(response => response.text())
            .then(csvText => {
                const lines = csvText.split('\n');
                // Skip header line
                const entries = lines.slice(1).filter(line => line.trim());
                
                if (entries.length === 0) {
                    setLogPreview(['No data available']);
                    return;
                }
                
                // Parse first timestamp to use as base time
                const firstEntry = entries[0].split(',');
                const baseTimeStr = firstEntry[0];
                const baseTime = parseTimeToSeconds(baseTimeStr);
                
                // Format entries for preview with relative time
                const preview = entries.map((line, index) => {
                    const [timeStr, ...messageParts] = line.split(',');
                    const message = messageParts.join(',');
                    const timeInSeconds = parseTimeToSeconds(timeStr);
                    
                    // Calculate relative time (first entry will be 0)
                    const relativeSeconds = timeInSeconds - baseTime;
                    const relativeTimeStr = formatSecondsToTime(relativeSeconds);
                    
                    return `${relativeTimeStr} (${timeStr}) - ${message}`;
                });
                
                setLogPreview(preview);
            })
            .catch(error => {
                console.error("Error reading CSV file:", error);
                setLogPreview(['Error loading file preview']);
            });
    };
    
    const startSimulation = () => {
        if (!selectedFile) return;
        
        // Clear previous items and timeouts
        setSelectedItems([]);
        timeoutIdsRef.current.forEach(id => clearTimeout(id));
        timeoutIdsRef.current = [];
        
        // Start simulation
        setIsRunning(true);
        
        // Fetch and process the CSV file
        fetch(`vnog_simulations/${selectedFile}`)
            .then(response => response.text())
            .then(csvText => {
                const lines = csvText.split('\n');
                // Skip header line
                const entries = lines.slice(1).filter(line => line.trim());
                
                if (entries.length === 0) return;
                
                // Parse first timestamp to use as base time
                const firstEntry = entries[0].split(',');
                const baseTimeStr = firstEntry[0];
                const baseTime = parseTimeToSeconds(baseTimeStr);
                
                // Schedule each message
                entries.forEach((line, index) => {
                    const [timeStr, ...messageParts] = line.split(',');
                    const message = messageParts.join(',');
                    const timeInSeconds = parseTimeToSeconds(timeStr);
                    
                    // For the first message, use 0 delay to show immediately
                    // For other messages, calculate relative time to the base time
                    const relativeTimeMs = index === 0 ? 0 : (timeInSeconds - baseTime) * 1000;
                    
                    // Schedule the message to be added
                    const timeoutId = window.setTimeout(() => {
                        const formattedMessage = `${timeStr} - ${message}`;
                        setSelectedItems(prev => [...prev, formattedMessage]);
                    }, relativeTimeMs);
                    
                    // Store the timeout ID so we can clear it if needed
                    timeoutIdsRef.current.push(timeoutId);
                });
            })
            .catch(error => {
                console.error("Error reading CSV file:", error);
                setIsRunning(false);
            });
    };
    
    const stopSimulation = () => {
        setIsRunning(false);
        
        // Clear all timeouts to stop messages from being added
        timeoutIdsRef.current.forEach(id => clearTimeout(id));
        timeoutIdsRef.current = [];
    };
    
    // Helper function to convert HH:MM:SS to seconds
    const parseTimeToSeconds = (timeStr: string): number => {
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds;
    };
    
    // Helper function to format seconds to HH:MM:SS
    const formatSecondsToTime = (totalSeconds: number): string => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };
    
    const openModal = () => {
        setIsModalOpen(true);
        if (!isInitialized) {
            setIsInitialized(true);
        }
    };
    
    const closeModal = () => {
        setIsModalOpen(false);
        // Don't reset state when closing the modal
    };
    
    return (
        <div>
            <button className="w-fit bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors" onClick={openModal}>Simulate</button>
            {isInitialized && (
                <Modal isOpen={isModalOpen} onRequestClose={closeModal}>
                    <div className="bg-white flex flex-col items-center relative min-w-[50vw] min-h-[50vh] overflow-y-auto z-100 rounded-md">
                        <div className="flex justify-between w-full px-8 pt-4 items-center">
                            <Title title="Simulator" />
                            <button 
                                onClick={closeModal}
                            >
                                <IconX className="w-4 h-4" />
                            </button>
                        </div>
                        <SimulatorContent 
                            selectedFile={selectedFile}
                            isRunning={isRunning}
                            logPreview={logPreview}
                            file_names={file_names}
                            handleFileChange={handleFileChange}
                            startSimulation={startSimulation}
                            stopSimulation={stopSimulation}
                        />

                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Simulator;
